import CommonDataGrid from "@/components/common/CommonDataGrid";
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import type { GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { usePermissions } from "@/hooks/usePermissions";
import { useAppSelector } from "@/store/hooks";
import type { RootState } from "@/store/store";
import { TurnAroundFilters, TurnAroundRow } from "@/types/turnaround";
import { formatDate } from "@/utils/date";

interface TurnaroundGridProps {
  filters: TurnAroundFilters | null;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  onEditTurnaround: (rowId: string, allRows: TurnAroundRow[]) => void;
  onDeleteTurnaround: (rowId: string, allRows: TurnAroundRow[]) => void;
  onBulkDelete: (allRows: TurnAroundRow[]) => void;
}

const TurnaroundGrid: React.FC<TurnaroundGridProps> = ({
  filters,
  paginationModel,
  onPaginationModelChange,
  onEditTurnaround,
  onDeleteTurnaround,
  onBulkDelete,
}) => {
  const { t } = useTranslation();
  const { getFeatureActions } = usePermissions();
  const viewConditionPermissions = getFeatureActions(
    "Devices",
    "View Condition Column",
    "Device List"
  );

  const turnAroundPermissions = getFeatureActions(
    'Turnaround',
    'Manage Turnaround',
    'Turnaround Detail Page'
  );

  // Get turnarounds data from Redux store - no fetching here, just reading
  const {
    data: allRows,
    loading,
    error,
  } = useAppSelector((state: RootState) => state.turnarounds);

  // Local UI state only
  const [rows, setRows] = useState<TurnAroundRow[]>([]);
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

  // Filter rows whenever allRows or filters change
  useEffect(() => {
    if (!filters) {
      setRows(allRows);
      setSelectedRowIds([]); // Clear selection on filter change
      return;
    }

    const filteredData = allRows.filter((row) => {
      // Status filter
      if (filters.status?.length) {
        const normalizedStatuses = filters.status.map((s: string) =>
          s.toLowerCase()
        );
        if (!normalizedStatuses.includes(row.status?.toLowerCase())) {
          return false;
        }
      }

      // Search filter (name)
      if (filters.search?.trim()) {
        const searchLower = filters.search.toLowerCase().trim();
        if (!row.name?.toLowerCase().includes(searchLower)) {
          return false;
        }
      }

      // Location filter
      if (filters.location?.length) {
        const locationNames = filters.location.map((l: string) =>
          l.toLowerCase()
        );
        if (!locationNames.includes(row.location?.toLowerCase())) {
          return false;
        }
      }

      // Start date filter - show turnarounds that start on or after this date
      if (filters.startDate) {
        const rowStartDate = new Date(row.start_date);
        const filterStartDate = new Date(filters.startDate);
        if (rowStartDate < filterStartDate) {
          return false;
        }
      }

      // End date filter - show turnarounds that end on or before this date
      if (filters.endDate) {
        const rowEndDate = new Date(row.end_date);
        const filterEndDate = new Date(filters.endDate);
        if (rowEndDate > filterEndDate) {
          return false;
        }
      }

      return true;
    });

    setRows(filteredData);
    setSelectedRowIds([]); // Clear selection on filter change
  }, [filters, allRows]);

  // Get current page row IDs for select-all-on-page
  const currentPageRowIds = useMemo(() => rows.map((row) => row.id), [rows]);
  const allSelectedOnPage =
    currentPageRowIds.length > 0 &&
    currentPageRowIds.every((id) => selectedRowIds.includes(String(id)));
  const someSelectedOnPage = currentPageRowIds.some((id) =>
    selectedRowIds.includes(String(id))
  );

  // Individual row checkbox
  const handleCheckboxChange = (rowId: string, checked: boolean) => {
    if (checked) {
      if (!selectedRowIds.includes(String(rowId))) {
        setSelectedRowIds([...selectedRowIds, String(rowId)]);
      }
    } else {
      setSelectedRowIds(selectedRowIds.filter((id) => id !== String(rowId)));
    }
  };

  // Header checkbox (select/deselect all on page)
  const handleHeaderCheckboxChange = (checked: boolean) => {
    if (!checked) {
      // Unchecking: remove all current page row IDs from selection
      setSelectedRowIds(
        selectedRowIds.filter(
          (id) => !currentPageRowIds.map(String).includes(String(id))
        )
      );
    } else {
      // Checking: add all current page row IDs
      const prevSet = new Set(selectedRowIds.map(String));
      const result = [...selectedRowIds.map(String)];
      for (let i = 0; i < currentPageRowIds.length; i++) {
        const idStr = String(currentPageRowIds[i]);
        if (!prevSet.has(idStr)) {
          result.push(idStr);
        }
      }
      setSelectedRowIds(result);
    }
  };

  // Custom checkbox column with header checkbox
  const checkboxColumn: GridColDef = {
    field: "select",
    headerName: "",
    renderHeader: () => {
      const indeterminate = !allSelectedOnPage && someSelectedOnPage;
      return (
        <Checkbox
          checked={allSelectedOnPage}
          indeterminate={indeterminate}
          onChange={(_, checked) => handleHeaderCheckboxChange(checked)}
          color={indeterminate ? "primary" : "secondary"}
          size="small"
          slotProps={{
            input: {
              "aria-label": `TurnAround Select All Rows`,
            },
          }}
        />
      );
    },
    width: 80,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params) => (
      <Checkbox
        checked={selectedRowIds.includes(params.row.id)}
        onChange={(_, checked) => handleCheckboxChange(params.row.id, checked)}
        color="secondary"
        size="small"
        slotProps={{
          input: {
            "aria-label": `Select row ${params.row.id}`,
          },
        }}
      />
    ),
  };

  // Actions column for CommonDataGrid
  const actionsColumn = useMemo(
    () => ({
      headerName: t("actions", "ACTIONS"),
      actions: (row: any) => {
        const actions = [];
        if (selectedRowIds.length === 0 && turnAroundPermissions.canEdit) {
          actions.push({
            label: t("editTurnaround", "Edit Turnaround"),
            onClick: () => {
              // console.log("Edit turnaround with ID:", row.id, rows);
              onEditTurnaround(row.id, rows);
            },
          });
        }
        if (turnAroundPermissions.canDelete) {
          actions.push({
            label: t("deleteTurnaround", "Delete Turnaround"),
            onClick: () => onDeleteTurnaround(row.id, rows),
          });
        }
        return actions;
      },
      width: 140,
    }),
    [selectedRowIds.length, t, rows, onEditTurnaround, onDeleteTurnaround, turnAroundPermissions]
  );

  const columns = useMemo(() => {
    const baseColumns: GridColDef[] = [
      checkboxColumn,
      {
        field: "name",
        headerName: t("name", "Name"),
        flex: 1,
        minWidth: 150,
        renderCell: (params) =>
          params.value && params.row.id ? (
            <Link
              to={`/turnarounds/${params.row.id}`}
              className="turnaround-link font-weight-medium text-primary"
              style={{ textDecoration: "none", color: "var(--primary)" }}
            >
              {params.value}
            </Link>
          ) : (
            "-"
          ),
      },
      {
        field: "status",
        headerName: t("status", "Status"),
        minWidth: 140,
        flex: 1,
        renderCell: (params) => (params.value ? params.value : "-"),
      },
      {
        field: "start_date",
        headerName: t("startDate", "Start Date"),
        minWidth: 140,
        flex: 1,
        renderCell: (params) => (params.value ? formatDate(params.value) : "-"),
      },
      {
        field: "end_date",
        headerName: t("endDate", "End Date"),
        minWidth: 140,
        flex: 1,
        renderCell: (params) => (params.value ? formatDate(params.value) : "-"),
      },
      {
        field: "devices",
        headerName: t("devices", "Devices"),
        minWidth: 120,
        flex: 1,
        renderCell: (params) => (params.value ? params.value : 0),
      },
      {
        field: "location",
        headerName: t("location", "Location"),
        minWidth: 140,
        flex: 1,
        renderCell: (params) => (params.value ? params.value : "-"),
      },
    ];
    return baseColumns;
  }, [checkboxColumn, t, viewConditionPermissions?.canView]);

  const handleBulkDeleteClick = () => {
    // Filter to get only the selected rows
    const selectedRows = rows.filter((row) => selectedRowIds.includes(row.id));
    onBulkDelete(selectedRows);
  };

  const handleClearSelection = () => {
    setSelectedRowIds([]);
  };

  return (
    <>
      {selectedRowIds.length > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            mb: 2,
            backgroundColor: "#f5f5f5",
            borderRadius: 1,
            border: "1px solid #e0e0e0",
          }}
        >
          <Typography variant="body2">
            {selectedRowIds.length}{" "}
            {selectedRowIds.length > 1
              ? t("turnaroundsSelectedCount", "turnarounds")
              : t("turnaroundSelectedCount", "turnaround")}{" "}
            {t("selected", "selected")}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            {turnAroundPermissions.canDelete && (
              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={handleBulkDeleteClick}
                data-testid="bulk-delete-button"
              >
                {t("deleteTurnarounds", "Delete Turnarounds")}
              </Button>
            )}
            <Button variant="text" size="small" onClick={handleClearSelection}>
              {t("clearSelection", "Clear Selection")}
            </Button>
          </Box>
        </Box>
      )}
      <CommonDataGrid
        columns={columns}
        rows={rows}
        loading={loading}
        pageName="Turnarounds"
        error={error}
        paginationMode="client"
        hideHeaderCount={true}
        noRowsMessage={
          error ? error : t("noTurnaroundsFound", "No turnarounds found.")
        }
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        pageSizeOptions={[25, 50, 75, 100]}
        hideFooterEntries={true}
        actionsColumn={actionsColumn}
      />
    </>
  );
};

export default TurnaroundGrid;
